/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */
import { UserAgentApplication } from "msal";

/**
 * Support for Internet Explorer
 */
export const isIE = (): boolean => {
  const ua = window.navigator.userAgent;
  const msie = ua.indexOf("MSIE ") > -1;
  const msie11 = ua.indexOf("Trident/") > -1;

  return msie || msie11;
};

/**
 * Determines if interactive method is needed to acquire token.
 * Since the silent token requests to Azure AD may fail for some reasons such as an expired Azure AD session or a password change.
 */
export const requiresInteraction = (errorMessage: any): boolean => {
  if (!errorMessage || !errorMessage.length) {
    return false;
  }

  return (
    errorMessage.indexOf("consent_required") > -1 ||
    errorMessage.indexOf("interaction_required") > -1 ||
    errorMessage.indexOf("login_required") > -1 ||
    errorMessage.indexOf("block_token_requests") > -1 ||
    errorMessage.indexOf("token_renewal_error") > -1
  );
};

/**
 * Define access scopes
 */
export const GRAPH_SCOPES = {
  OPENID: "openid",
  PROFILE: "profile",
  USER_READ: "User.Read",
  USER_READBASIC_ALL: "User.ReadBasic.All",
};

/**
 * Define access scopes for Maven.
 */
export const MAVEN_SCOPES = {
  USER_ACCESS: process.env.REACT_APP_API_APPID
    ? "api://" + process.env.REACT_APP_API_APPID + "/user_impersonate"
    : "api://790b3d39-99e5-4590-80d7-916adf11faef/user_impersonate",
};

export const GRAPH_ENDPOINTS = {
  ME: "https://graph.microsoft.com/v1.0/me",
  USER_BY_EMAIL: "https://graph.microsoft.com/v1.0/users/",
};

/**
 * Define Graph request with scope info.
 * NOTE: Also defining extra scope to enable silently getting Maven token from cache when needed.
 */
export const GRAPH_REQUESTS = {
  LOGIN: {
    scopes: [
      GRAPH_SCOPES.OPENID,
      GRAPH_SCOPES.PROFILE,
      GRAPH_SCOPES.USER_READ,
      GRAPH_SCOPES.USER_READBASIC_ALL,
    ],
    extraScopesToConsent: [MAVEN_SCOPES.USER_ACCESS],
  },
};

/**
 * Define Maven api request scopes.
 */
export const MAVEN_REQUESTS = {
  LOGIN: {
    scopes: [MAVEN_SCOPES.USER_ACCESS],
  },
};

/**
 * Fetch MS graph data.
 * @param {string} url The Graph endpoint.
 * @param {string} accessToken The access token.
 * @returns {Promise<any>} .
 */
export const fetchMsGraph = async (url: any, accessToken: any) => {
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  return response.json();
};

/**
 * Core MSAL configuration
 * Instantiate UserAgentApplication  with a configuration object - this intializes MSAL authentication context
 * TODO - revisit caching options and port to config file after refactoring
 */
export const msalApp = new UserAgentApplication({
  auth: {
    clientId: process.env.REACT_APP_AAD_CLIENTID || "964edd73-cfe4-4cec-8b36-68cb5c2f5cc5",
    authority: "https://login.microsoftonline.com/" + process.env.REACT_APP_AAD_TENANTID,
    validateAuthority: true,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: "localStorage",
    storeAuthStateInCookie: isIE(),
  },
  system: {
    navigateFrameWait: 0,
  },
});
